import { PlusIcon } from '@heroicons/react/16/solid'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Skeleton from 'react-loading-skeleton'
import { ActivityIndicator } from '../../../components'
import CrossMatchingTable from '../../../components/cross-matching-table'
import Button from '../../../components/tailwind/Button'
import { Heading } from '../../../components/ui/Heading'
import { useNotificationContext } from '../../../context'
import { query } from '../../../graphql'
import { notifyBugsnag } from '../../../helpers'
import { useSelectionGuard } from '../../pricing/hooks'
import {
  LIMIT_DIALOG_ACTIONS,
  usePricingDialog
} from '../../pricing/pricing-dialog-context'
import { useDiscover } from '../hooks/useDiscover'
import { useSelectionParams } from '../hooks/useSelectionParams'
import EnableFlexSwitch from './EnableFlexSwitch'
import EvaluationCard from './flexibleMatching/EvaluationCard'
import {
  AnalyzeFlexibleIcon,
  AnalyzeTeamsIcon,
  CompareTalentsIcon
} from './flexibleMatching/EvaluationIcons'

export const SelectionSection = ({
  title,
  items,
  renderItem,
  addMoreTitle,
  onClickAddMore,
  disabled = false,
  isLoading
}) => (
  <div className='flex flex-col gap-y-4'>
    <Heading level={3} className='text-darkBlue'>
      {isLoading ? (
        <Skeleton className='max-w-64' />
      ) : (
        `${title} (${items.length})`
      )}
    </Heading>
    {!isLoading && (
      <div className='flex flex-row flex-wrap gap-2'>
        {items.map(renderItem)}
        <Button.SecondaryXL
          text={addMoreTitle}
          onClick={onClickAddMore}
          disabled={disabled}
          icon={<PlusIcon className='size-4 text-buttonSecondaryBlue' />}
          className='h-fit flex-none self-center'
        />
      </div>
    )}
  </div>
)

SelectionSection.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
  renderItem: PropTypes.func.isRequired,
  addMoreTitle: PropTypes.string.isRequired,
  onClickAddMore: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool
}

export default function FlexibleMatchingSection({
  className,
  selectedTalents,
  selectedCareers
}) {
  const { t } = useTranslation()
  const { isFlexibleMatchingEnabled, setIsFlexibleMatchingEnabled } =
    useDiscover()
  const { success, error } = useNotificationContext()
  const { updateSelection } = useSelectionParams()
  const { checkSelection } = useSelectionGuard()
  const { openLimitDialog } = usePricingDialog()

  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [matchBatchIfYouCan, setMatchBatchIfYouCan] = useState(null)

  // Talentspeicher. Gesperrte Talente laufen nicht in die Analyse. Solange
  // nicht gezählt ist, wartet der Button. In legacy und preview ist nichts
  // gesperrt.
  const selectionCheck = useMemo(
    () => checkSelection(selectedTalents),
    [checkSelection, selectedTalents]
  )

  const validateAndParseResponse = (response) => {
    if (!response || response === 'undefined') {
      throw new Error('Invalid response from matchBatchIfYouCan query')
    }

    try {
      return JSON.parse(response)
    } catch (parseError) {
      throw new Error(
        `Failed to parse analysis response: ${parseError.message}`
      )
    }
  }

  const runAnalysis = async (talentIds) => {
    setIsAnalyzing(true)

    try {
      const variables = {
        careers: selectedCareers,
        users: talentIds
      }

      const response = await query({
        query: 'matchBatchIfYouCan',
        variables
      })

      const parsedData = validateAndParseResponse(response)
      setMatchBatchIfYouCan(parsedData)

      success(t('discover.flexible_matching_section.analysis_successfull'))
    } catch (err) {
      error('An error occurred during analysis: ' + err.message)
      notifyBugsnag(err)
    } finally {
      setIsAnalyzing(false)
    }
  }

  const onClickStartAnalysis = async () => {
    const check = checkSelection(selectedTalents)
    if (check.pending) return
    if (check.allowed) {
      await runAnalysis(selectedTalents)
      return
    }

    // „Gesperrte abwählen" nur, wenn danach Talente übrig sind. Danach läuft
    // die Analyse mit der übrigen Auswahl weiter, außer es bleibt ein Talent
    // zu einer Stelle, dann zeigt Discover die Einzelansicht.
    const remaining = selectedTalents.filter((id) => !check.lockedIds.has(id))
    openLimitDialog('talentStorage', {
      action: LIMIT_DIALOG_ACTIONS.SELECTION,
      source: 'discover_flexible_matching',
      count: check.lockedCount,
      ...(remaining.length > 0 && {
        onDeselect: () => {
          updateSelection(remaining, selectedCareers)
          if (remaining.length > 1 || selectedCareers.length > 1) {
            runAnalysis(remaining)
          }
        }
      })
    })
  }

  const evaluationCards = [
    {
      icon: <AnalyzeFlexibleIcon />,
      titleKey:
        'discover.flexible_matching_section.evaluation.analyze_flexible.title',
      descriptionKey:
        'discover.flexible_matching_section.evaluation.analyze_flexible.description'
    },
    {
      icon: <CompareTalentsIcon />,
      titleKey:
        'discover.flexible_matching_section.evaluation.compare_talents.title',
      descriptionKey:
        'discover.flexible_matching_section.evaluation.compare_talents.description'
    },
    {
      icon: <AnalyzeTeamsIcon />,
      titleKey:
        'discover.flexible_matching_section.evaluation.analyze_teams.title',
      descriptionKey:
        'discover.flexible_matching_section.evaluation.analyze_teams.description'
    }
  ]

  return (
    <div className={clsx('w-full rounded-lg bg-white p-6', className)}>
      <div className='flex flex-row items-start justify-between'>
        <h2 className='text-2xl font-semibold text-darkBlue'>
          {t('discover.flexible_matching_section.title')}
        </h2>
        <EnableFlexSwitch
          enabled={isFlexibleMatchingEnabled}
          setEnabled={setIsFlexibleMatchingEnabled}
        />
      </div>
      {/* {selectedCareers.length === 1 && selectedTalents.length === 1 && (
        <div className='mt-4 flex flex-row gap-x-4'>
          {careers.length > 0 && (
            <CareerCard
              title={careers[0].title}
              careerId={careers[0].id}
              idLabel={t('discover.match_overview_section.career_id', {
                career_id: ''
              }).replace(': ', '')}
              idTooltip={`${t('discover.match_overview_section.career_id_full')}: ${careers[0].id}]} (${t('discover.match_overview_section.click_to_copy')})`}
              changeCareerLabel={t(
                'discover.match_overview_section.change_career'
              )}
              onIdClick={onIdClick}
              onClickChangeCareer={() => {
                removeCareer(career.id)
                setAddCareerModalOpen(true)
              }}
              onClickClose={() => {
                setCareers([])
              }}
              hasCompletedOnboarding={hasCompletedOnboarding}
            />
          )}
          {users.length > 0 && (
            <TalentCard
              title={extractTalentName(talent, t)}
              onClickClose={() => {
                removeTalent(talent.id)
              }}
              changeTalentLabel={t(
                'discover.match_overview_section.change_talent'
              )}
              talentId={talent.id}
              onIdClick={onIdClick}
              onClickChangeTalent={() => {
                removeTalent(talent.id)
                setInviteTalentOpen(true)
              }}
              idLabel={t('discover.match_overview_section.talent_id', {
                talent_id: ''
              }).replace(': ', '')}
              hasCompletedOnboarding={hasCompletedOnboarding}
            />
          )}
          <AddTalentOrCareerCard />
        </div>
      )}
      {shouldShowSelections && (
        <div className='mt-6 space-y-4'>
          <SelectionSection
            title={t('discover.flexible_matching_section.selected_talents')}
            items={selectedTalents}
            renderItem={renderTalentCard}
            addMoreTitle={t('discover.flexible_matching_section.add_talent')}
            onClickAddMore={() => setInviteTalentOpen(true)}
            disabled={!hasCompletedOnboarding}
          />
          <SelectionSection
            title={t('discover.flexible_matching_section.selected_careers')}
            items={selectedCareers}
            renderItem={renderCareerCard}
            addMoreTitle={t('discover.flexible_matching_section.add_career')}
            onClickAddMore={() => setAddCareerModalOpen(true)}
            disabled={!hasCompletedOnboarding}
          />
        </div>
      )} */}

      <div className='my-6 flex items-center justify-center'>
        <Button.PrimaryXL
          text={t('discover.flexible_matching_section.start_analysis')}
          onClick={onClickStartAnalysis}
          isLoading={isAnalyzing || selectionCheck.pending}
          disabled={
            selectedCareers.length === 1 && selectedTalents.length === 1
          }
        />
      </div>
      <div className='border-t border-lightGrey pt-6'>
        <h2 className='text-2xl font-semibold text-darkBlue'>
          {t('discover.evaluation_section.title')}
        </h2>
        {!isAnalyzing && matchBatchIfYouCan == null && (
          <div className='mx-auto mt-6 max-w-96 text-center text-sm font-normal leading-[130%] text-rockGrey'>
            {t('discover.flexible_matching_section.evaluation.description')}
          </div>
        )}
        <div className='p-6'>
          {isAnalyzing && (
            <ActivityIndicator className='flex justify-center py-4' />
          )}
          {!isAnalyzing && matchBatchIfYouCan == null && (
            <div className='grid grid-cols-1 gap-6 md:grid-cols-3'>
              {evaluationCards.map((card, index) => (
                <EvaluationCard
                  key={index}
                  icon={card.icon}
                  title={t(card.titleKey)}
                  description={t(card.descriptionKey)}
                />
              ))}
            </div>
          )}
          {matchBatchIfYouCan && (
            <div>
              <CrossMatchingTable
                matchingData={matchBatchIfYouCan}
                talents={selectedTalents}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

FlexibleMatchingSection.propTypes = {
  className: PropTypes.string,
  selectedTalents: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedCareers: PropTypes.arrayOf(PropTypes.string).isRequired
}
